import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { StockDividendsCard } from '@pages/Traders-Room/Stock-Dividends/StockDividendsCard'
import { useStockDividendsFetch } from '@pages/Traders-Room/Stock-Dividends/hooks/useStockDividendsFetch'
import { useStockDividendsFile } from '@pages/Traders-Room/Stock-Dividends/hooks/useStockDividendsFile'
import { useStockDividendsSearch } from '@pages/Traders-Room/Stock-Dividends/hooks/useStockDividendsSearch'

import { PageSection } from '@ui/components/PageSection'
import { Pagination } from '@ui/v2/Pagination/Pagination'
import { FilterOptions, TableFilter } from '@ui/v2/TableFilter/TableFilter'

import { useWindowResize } from '@utils/domUtils'

import { FilterQueryProps } from '@model/FilterFields'

import {
  ScrollToIds,
  useScrollAfterLoad,
  useScrollIntoViewOnPagingEntriesChange,
} from '@hooks/useScrollToElementIds'

import { StockDividendsTable } from './StockDividendsTable'

export const StockDividendsPage: React.FC = () => {
  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const search = useStockDividendsSearch()

  const { data, meta, pageQuery, setPageQuery, hasInitialResults, isLoading } =
    useStockDividendsFetch(search)

  const [isFilterVisible, setIsFilterVisible] = useState(true)
  const generateDocumentFile = useStockDividendsFile(data)

  const [isPaginationEntrySelected, setIsPaginationEntrySelected] = useState(false)
  useScrollIntoViewOnPagingEntriesChange(
    ScrollToIds.StockDividendsHeader,
    isPaginationEntrySelected,
    isLoading,
    setIsPaginationEntrySelected
  )
  useScrollAfterLoad(ScrollToIds.StockDividendsHeader, isLoading, meta?.pageSize)

  const handleSearchFilters = useCallback(
    (searchFilters?: FilterQueryProps) => {
      if (!searchFilters) return
      search.setSearch(searchFilters)
      setPageQuery?.({ ...pageQuery, pageIndex: 1 })
    },
    [search, pageQuery, setPageQuery]
  )

  useEffect(() => {
    if (!setPageQuery) return
    if (pageQuery && pageQuery?.pageIndex !== 1) {
      setPageQuery({ ...pageQuery, pageIndex: 1 })
    }
  }, [search.debouncedSearchTerm])

  return (
    <div>
      <PageSection
        id={ScrollToIds.StockDividendsHeader}
        title={t('Tabs.Stock Dividends')}
        documentFile={generateDocumentFile}
        searchField={{
          onChange: (v) => {
            search.setSearchTerm(v)
          },
          value: search.searchTerm,
          disabled: false,
          canClear: true,
          label: search.placeholder,
        }}
        onFilterClick={() => setIsFilterVisible((s) => !s)}
      />
      {hasInitialResults && (
        <TableFilter
          isVisible={isFilterVisible}
          searchFilters={search.search}
          filterOptions={[FilterOptions.Date]}
          allowFutureDates
          setSearchFilters={handleSearchFilters}
        />
      )}

      {isMobile ? (
        data.map((card) => <StockDividendsCard data={card} />)
      ) : (
        <StockDividendsTable data={data} isLoading={isLoading} />
      )}

      {meta && hasInitialResults && (
        <Pagination
          scrollToHeaderId={ScrollToIds.StockDividendsHeader}
          pageData={meta}
          isLoading={isLoading}
          onPageChanged={(pageIndex: number, pageSize?: number) => {
            if (pageSize && pageSize !== meta?.pageSize) {
              setIsPaginationEntrySelected(true)
            }
            setPageQuery?.({ ...pageQuery, pageIndex, pageSize })
          }}
        />
      )}
    </div>
  )
}
